import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LINK_TYPE_ICONS, LinkIcon } from './NoteTagIcons';
import './notes.css';

// Where clicking a note's link pill takes the user — shared by My Notes
// (MyNotesPage.js's handleNoteClick) and the Ria chat note links below, so a
// note saved via chat navigates exactly like one added manually. Only uses the
// explicit `transactionId` field for transaction-scoped notes (never a
// TRANSACTION note's `linkId` alone) — for notes created from Timeline/
// Parties/Documents, linkId is a milestone/participant/document ID, not a
// transaction ID. PROPERTY is the one link type whose linkId is a real,
// directly-navigable ID. Returns null for CONVERSATION and untagged notes.
export const getNoteRoute = (note) => {
  if (!note) return null;
  if (note.linkType === 'PROPERTY' && note.linkId) {
    return { path: `/property/${note.linkId}` };
  }
  if (note.transactionId) {
    const activeTab = note.linkType === 'DOCUMENT' ? 'docs'
      : note.linkType === 'PARTY' ? 'chat'
      : 'timeline';
    return { path: `/transaction/${note.transactionId}`, state: { activeTab } };
  }
  return null;
};

// Notes Ria saved in a chat turn: structured_data of type "note_created" on
// either a live /ria/chat response (ria_service._extract_structured_data) or
// a message reloaded from conversation history (rebuilt from the stored
// create_note results by firestore_service._note_structured_data). Raw
// create_note tool_results are also accepted, in case a caller passes them.
// Returns [] otherwise.
export const extractRiaNoteLinks = (source) => {
  if (!source) return [];
  try {
    const sd = source.structured_data;
    if (sd?.type === 'note_created' && Array.isArray(sd.data?.notes)) {
      return sd.data.notes.filter((n) => n && n.created);
    }
    if (Array.isArray(source.tool_results)) {
      return source.tool_results
        .filter((tr) => tr?.name === 'create_note' && !tr.error && tr.result?.created)
        .map((tr) => tr.result);
    }
  } catch (err) {
    console.warn('[Ria] Failed to parse note links:', err);
  }
  return [];
};

// Mirrors NoteCard.js's MERGES_WITH_TRANSACTION_NAME: sub-item links read as
// "Transaction – Item". Skipped when both names are the same (a note Ria
// linked straight to a transaction) to avoid "Maple Ct – Maple Ct".
const MERGES_WITH_TRANSACTION_NAME = new Set(['TRANSACTION', 'DOCUMENT', 'PARTY']);

const pillLabel = (note) => {
  const label = note.linkLabel || '';
  if (note.transactionName && MERGES_WITH_TRANSACTION_NAME.has(note.linkType)
    && note.transactionName !== label) {
    return `${note.transactionName} – ${label}`;
  }
  return label;
};

/**
 * Clickable link(s) under a Ria reply that saved a note: jumps to what the
 * note is linked to (same destination as the note's pill on My Notes), or to
 * My Notes itself for untagged/conversation notes, which have no other page.
 */
export default function RiaNoteLinks({ notes, className = '' }) {
  const navigate = useNavigate();
  if (!Array.isArray(notes) || notes.length === 0) return null;

  return (
    <div className={`rn-ria-note-links ${className}`.trim()}>
      {notes.map((note, i) => {
        const route = getNoteRoute(note);
        const LinkTypeIcon = route ? (LINK_TYPE_ICONS[note.linkType] || LinkIcon) : LinkIcon;
        const label = route ? pillLabel(note) : 'View in My Notes';
        const handleClick = (e) => {
          e.stopPropagation();
          if (route) navigate(route.path, route.state ? { state: route.state } : undefined);
          else navigate('/my-notes');
        };
        return (
          <button
            type="button"
            key={note.noteId || i}
            className="rn-ria-note-link"
            onClick={handleClick}
            title={route ? 'Open' : 'Open My Notes'}
          >
            <LinkTypeIcon /> {label || 'Open note'}
          </button>
        );
      })}
    </div>
  );
}
